"use client";

import type { MouseEvent, ReactNode } from "react";
import { useParams } from "next/navigation";

import { useChainDown } from "@/features/blockchain-status/store";
import { Spinner } from "../ui";

import "./action.css";

/*
 * THE PRIMARY ACTION — one button grammar for every form and money dialog.
 *
 * Four states, and each one tells the reader what is going on:
 *   ready    — pressable;
 *   blocked  — something is still missing. It LOOKS off but stays pressable:
 *              a press runs the form's own handler, whose validation puts the
 *              message under each field and scrolls to the first one (or calls
 *              `onBlocked` instead). The line beside it (`ActionWhy`) says what
 *              is left;
 *   busy     — the request is out: the spinner and the waiting label take the
 *              label's place, every press is swallowed, and the button keeps
 *              its colour and its focus (a natively disabled button would
 *              drop the focus to <body>);
 *   disabled — off with nowhere to go from here (a native `disabled`).
 *
 * Both faces sit in one grid cell and only one is visible, so the button is
 * always as wide as the wider of the two: nothing beside it moves when the
 * label turns into «در حال …».
 */
type ActionProps = {
  children: ReactNode;
  icon?: ReactNode;
  busy?: boolean;
  busyLabel?: ReactNode;
  blocked?: boolean;
  disabled?: boolean;
  onBlocked?: () => void;
  type?: "button" | "submit";
  form?: string;
  className?: string;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  whyId?: string;
  title?: string;
  ariaLabel?: string;
  /**
   * The action sends something to the blockchain (a transaction, a purchase, a sale, a mint).
   * While `GET /blockchain-status` says the chain is down, it is off, and its title carries the
   * backend's words; the site-wide notice says the same above the page.
   */
  chain?: boolean;
};

export function ActionButton({ chain, ...props }: ActionProps) {
  return chain ? <ChainAction {...props} /> : <ActionButtonBase {...props} />;
}

function ChainAction(props: Omit<ActionProps, "chain">) {
  const params = useParams<{ lang?: string }>();
  const { down, message } = useChainDown(params?.lang === "en" ? "en" : "fa");
  if (!down) return <ActionButtonBase {...props} />;
  return <ActionButtonBase {...props} disabled blocked={false} title={message ?? props.title} />;
}

function ActionButtonBase({
  children, icon, busy = false, busyLabel, blocked = false, disabled = false, onBlocked,
  type = "button", form, className, onClick, whyId, title, ariaLabel,
}: {
  children: ReactNode;
  icon?: ReactNode;
  busy?: boolean;
  /** The waiting form of the same verb — «در حال ساخت…». Defaults to the label. */
  busyLabel?: ReactNode;
  blocked?: boolean;
  disabled?: boolean;
  /** A blocked press on a `type="button"`: lead the reader to what is missing. */
  onBlocked?: () => void;
  type?: "button" | "submit";
  form?: string;
  /** The variant classes — `v2-btn` and `v2-focus` are added here. */
  className?: string;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  /** The id of the line that says why it is blocked. */
  whyId?: string;
  title?: string;
  ariaLabel?: string;
}) {
  const state = busy ? "busy" : blocked ? "blocked" : "ready";
  return (
    <button
      type={type}
      form={form}
      title={title}
      aria-label={ariaLabel}
      className={`v2-btn act v2-focus${className ? ` ${className}` : ""}`}
      data-state={state}
      disabled={disabled && !busy}
      aria-disabled={busy || blocked || undefined}
      aria-busy={busy || undefined}
      aria-describedby={blocked && whyId ? whyId : undefined}
      onClick={(e) => {
        if (busy) { e.preventDefault(); return; }
        // Blocked is a look and a reason, never a dead button: the press goes on to the
        // form's own handler, whose validation marks the fields and leads to the first.
        if (blocked && onBlocked) { e.preventDefault(); onBlocked(); return; }
        onClick?.(e);
      }}
    >
      <span className="act-face" data-on={busy ? undefined : true} aria-hidden={busy || undefined}>{icon}{children}</span>
      <span className="act-face" data-on={busy || undefined} aria-hidden={busy ? undefined : true}><Spinner />{busyLabel ?? children}</span>
    </button>
  );
}

/** The one line beside a blocked action: what is still missing, in plain words. */
export function ActionWhy({ id, children, className }: { id?: string; children?: ReactNode; className?: string }) {
  if (!children) return null;
  return <p id={id} className={`act-why${className ? ` ${className}` : ""}`} role="status">{children}</p>;
}

/** The line beside a blocked action, from the fields still missing (lib/still-needed). */
export { stillNeeded } from "@/lib/still-needed";

/**
 * The form's fields while its request is out: they cannot be changed under it
 * (`inert` takes them out of focus and the pointer at once), and they step back
 * a little so the eye goes to the button that is working.
 */
export function lockWhile(busy: boolean): { inert?: boolean; "data-busy-lock"?: true } {
  return busy ? { inert: true, "data-busy-lock": true } : {};
}
